import React, { useState } from 'react';
import {
  Text,
  VStack,
  Button,
  Input,
  FormControl,
  FormLabel,
  Select,
  useToast,
  HStack,
} from '@chakra-ui/react';
import ModalShell from './ui/ModalShell';
import { ff } from '../theme';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { countryOptions } from '../types';
import { formatPhoneNumber, getPhoneAuthErrorMessage } from '../utils/authUtils';
import { sendLinkPhoneNumberCode, verifyPhoneCode } from '../services/firebaseService';
import { authApi } from '../services/api/auth';

interface PhoneVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({ 
  isOpen, 
  onClose, 
}) => {
  const [user] = useAuthState(auth);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'phone' | 'verify'>('phone');
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const toast = useToast();

  const handleSendCode = async () => {
    if (!phoneNumber.trim()) {
      toast({
        title: "Phone number required",
        description: "Please enter your phone number",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const fullPhoneNumber = formatPhoneNumber(phoneNumber, countryCode);
      const result = await sendLinkPhoneNumberCode(fullPhoneNumber);
      setConfirmationResult(result);
      setStep('verify');
      toast({
        title: "Code sent",
        description: "Verification code sent to your phone",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast({
        title: "Error",
        description: getPhoneAuthErrorMessage(error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      toast({
        title: "Verification code required",
        description: "Please enter the verification code",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const isValid = await verifyPhoneCode(confirmationResult, verificationCode);
      
      if (!isValid) {
        toast({
          title: "Invalid code",
          description: "The verification code is incorrect. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Update phone number via backend (user is authenticated)
      const fullPhoneNumber = formatPhoneNumber(phoneNumber, countryCode);
      
      // Get email from current user
      const email = user?.email;
      if (!email) {
        toast({
          title: "Error",
          description: "Unable to get user email. Please try signing in again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      // Get invite token from session storage if available (from collab invite flow)
      const inviteToken = sessionStorage.getItem('collabInviteToken') || undefined;
      const collabId = sessionStorage.getItem('collabId') || undefined;
      
      try {
        await authApi.updateInfluencerPhone(fullPhoneNumber, email, inviteToken, collabId);
        
        // Clear session storage after successful verification
        if (inviteToken) {
          sessionStorage.removeItem('collabInviteToken');
          sessionStorage.removeItem('collabId');
        }
        
        toast({
          title: "Phone number linked",
          description: "You can now access the collab offer details",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        onClose();
      } catch (error) {
        console.error('Error updating phone number:', error);
        toast({
          title: "Error",
          description: "Failed to verify phone number. Please contact support.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      toast({
        title: "Error",
        description: "Failed to verify code. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep('phone');
    setPhoneNumber('');
    setVerificationCode('');
    setConfirmationResult(null);
    onClose();
  };

  const isPhoneStep = step === 'phone';

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={isPhoneStep ? 'Verify Your Phone Number' : 'Enter Verification Code'}
      primary={{
        label: isPhoneStep ? 'Send Verification Code' : 'Verify Code',
        loadingText: isPhoneStep ? 'Sending...' : 'Verifying...',
        onClick: isPhoneStep ? handleSendCode : handleVerifyCode,
        isLoading,
        arrow: true,
      }}
    >
      <VStack spacing={5} align="stretch">
        {isPhoneStep ? (
          <>
            <Text fontSize="md" color={ff.text2}>
              We need to verify your phone number to show you the full offer details.
            </Text>
            <FormControl>
              <FormLabel>Phone Number</FormLabel>
              <HStack spacing={2}>
                <Select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} width="30%">
                  {countryOptions.map(option => (
                    <option key={option.code} value={option.code}>
                      {option.code}
                    </option>
                  ))}
                </Select>
                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  flex={1}
                />
              </HStack>
            </FormControl>
          </>
        ) : (
          <>
            <Text fontSize="md" color={ff.text2}>
              Enter the 6-digit code sent to {countryCode} {phoneNumber}
            </Text>
            <FormControl>
              <FormLabel>Verification Code</FormLabel>
              <Input
                type="text"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={6}
                textAlign="center"
                fontSize="lg"
                letterSpacing="0.2em"
              />
            </FormControl>
            <Button variant="ghost" color={ff.text2} onClick={() => setStep('phone')} size="sm">
              Change Phone Number
            </Button>
          </>
        )}
      </VStack>
    </ModalShell>
  );
};

export default PhoneVerificationModal;
